import { Check } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../../../components/Button';
import { Dialog } from '../../../components/Dialog';
import { cn } from '../../../lib/cn';
import { ticketErrorRO } from '../ticketCopy';
import { useSetLabelColorMutation } from '../tickets.api';

import { LABEL_COLOR_KEYS, LABEL_COLORS } from './LabelChip';

type Props = {
    label: string | null;
    current?: string | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

// 16-swatch colour picker for a label. Manager/Admin only (gating is on the
// trigger + the API). Saving recolours the label everywhere it's used.
export function LabelColorDialog({ label, current, open, onOpenChange }: Props) {
    const [setColor, { isLoading }] = useSetLabelColorMutation();

    const choose = async (color: string) => {
        if (!label) return;
        try {
            await setColor({ label, color }).unwrap();
            onOpenChange(false);
        } catch (e) {
            toast.error(ticketErrorRO(e, 'Eroare la schimbarea culorii.'));
        }
    };

    return (
        <Dialog
            open={open}
            closeOnBackdrop={false}
            onOpenChange={onOpenChange}
            maxWidth="max-w-sm"
            title="Culoare eticheta"
            description={label ? `Alege o culoare pentru "${label}".` : undefined}
            footer={
                <Button type="button" variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
                    Inchide
                </Button>
            }
        >
            <div className="grid grid-cols-4 justify-items-center gap-3 sm:grid-cols-8">
                {LABEL_COLOR_KEYS.map((key) => {
                    const selected = current === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => choose(key)}
                            disabled={isLoading}
                            aria-label={key}
                            title={key}
                            className={cn(
                                'flex h-10 w-10 items-center justify-center rounded-full ring-2 ring-offset-2 ring-offset-white transition disabled:opacity-50 dark:ring-offset-gray-800',
                                LABEL_COLORS[key].swatch,
                                selected
                                    ? 'ring-gray-900 dark:ring-white'
                                    : 'ring-transparent hover:ring-gray-300 dark:hover:ring-gray-600',
                            )}
                        >
                            {selected && <Check aria-hidden="true" className="h-5 w-5 text-white" />}
                        </button>
                    );
                })}
            </div>
        </Dialog>
    );
}
